/**
 * Un proveedor: lo que se sabe de él y lo que se le ha comprado.
 *
 * The owner asked to open a supplier from «Proveedores» and see everything about them, and to
 * change what is known of them, the owner alone (2026-10-16). What is known is four fields
 * (`proveedor.creado`, `proveedor.actualizado`); the rest of the page is read from the purchases
 * and what is owed, as «Mercadería comprada» and «Cuentas por pagar» read them, so the figures
 * agree with those screens'.
 */

import { esDeudaAnterior, type Compra, type Gasto, type Proveedor } from '@abastece/core';
import type { Centavos, PayloadInputOf } from '@abastece/shared';
import { porPagar, type PorPagar } from './cuadrar.js';
import { ordenarCompras, resumenCompras } from './historial.js';

/** The form's four fields, as typed. */
export interface CamposProveedor {
  nombre: string;
  telefono: string;
  contacto: string;
  notas: string;
}

export const camposDeProveedor = (p: Pick<Proveedor, 'nombre' | 'telefono' | 'contacto' | 'notas'>): CamposProveedor => ({
  nombre: p.nombre,
  telefono: p.telefono ?? '',
  contacto: p.contacto ?? '',
  notas: p.notas ?? '',
});

export type CambioDeProveedor = PayloadInputOf<'proveedor.actualizado'>;

/**
 * What changed, as `proveedor.actualizado` says it: only the fields that did, trimmed, and a field
 * emptied as null, which the books clear. Null when nothing changed, and when the name was emptied:
 * a supplier always has one.
 */
export function cambioDeProveedor(
  antes: Pick<Proveedor, 'id' | 'nombre' | 'telefono' | 'contacto' | 'notas'>,
  campos: CamposProveedor,
): CambioDeProveedor | null {
  const nombre = campos.nombre.trim();
  if (!nombre) return null;
  const opcional = (s: string) => s.trim() || null;
  const cambio: CambioDeProveedor = { proveedorId: antes.id };
  if (nombre !== antes.nombre) cambio.nombre = nombre;
  if (opcional(campos.telefono) !== (antes.telefono ?? null)) cambio.telefono = opcional(campos.telefono);
  if (opcional(campos.contacto) !== (antes.contacto ?? null)) cambio.contacto = opcional(campos.contacto);
  if (opcional(campos.notas) !== (antes.notas ?? null)) cambio.notas = opcional(campos.notas);
  return Object.keys(cambio).length > 1 ? cambio : null;
}

export interface CuentaDeProveedor {
  /**
   * Its purchases, the latest first, the voided among them: what «Mercadería comprada» lists for it.
   * What was owed from before brought no goods, and is only in `porPagar`.
   */
  compras: Compra[];
  /**
   * How many stand and what they came to, the voided left out of both: the count «Proveedores» gives
   * the supplier, and «Mercadería comprada»'s total for it.
   */
  resumen: { n: number; total: Centavos };
  /** The day of the latest that stands. */
  ultima: string | null;
  /** What is still owed to it, purchases and expenses bought on credit, the first due first. */
  porPagar: PorPagar[];
  debe: Centavos;
  /** Something owed past the day it was due. */
  vencido: boolean;
}

export function cuentaDeProveedor(
  proveedorId: string,
  compras: readonly Compra[],
  gastos: readonly Gasto[],
  hoy: string,
): CuentaDeProveedor {
  const suyas = compras.filter((c) => c.proveedorId === proveedorId);
  const conMercaderia = ordenarCompras(suyas.filter((c) => !esDeudaAnterior(c)), '');
  const deudas = porPagar(suyas, gastos.filter((g) => g.proveedorId === proveedorId));
  return {
    compras: conMercaderia,
    resumen: resumenCompras(conMercaderia.filter((c) => !c.anulada)),
    ultima: conMercaderia.find((c) => !c.anulada)?.fecha ?? null,
    porPagar: deudas,
    debe: deudas.reduce((a, d) => a + d.pendiente, 0) as Centavos,
    vencido: deudas.some((d) => d.vence !== null && d.vence < hoy),
  };
}
